import { Box, Button, Grid, Group, Modal, Text } from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { clsx } from 'clsx'
import { useCallback, useRef, useState } from 'react'

import { MarkdownEditor } from '@/components/MarkdownEditor'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as commonClasses from '@/styles/componentCommons.css'

export interface AddStepDataContentModalProps extends CMFormSection {
  initialContent?: string
}

export function AddStepDataContentModal(props: AddStepDataContentModalProps) {
  const { uiStore } = useGlobalState()

  const [initialContent] = useState(props.initialContent ?? props.stepContentQuery.data?.content ?? '')
  const contentRef = useRef(initialContent)
  const [contentError, setContentError] = useState<string | null>(null)

  const handleContentChange = useCallback(
    (markdown: string) => {
      contentRef.current = markdown
      if (contentError) setContentError(null)
    },
    [contentError],
  )

  async function createCM() {
    const markdownContent = contentRef.current

    if (!markdownContent.trim()) {
      setContentError("The content can't be blank.")
      return
    }

    if (props.stepId) {
      await props.contentUpdateMutation?.mutateAsync({
        step_id: props.stepId,
        text: markdownContent,
      })
    } else {
      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]

      const result = await props.stepCreateMutation.mutateAsync({
        task_id: props.taskId!,
        preceding_step_id: lastStep?.stepId,
        content: markdownContent,
      })

      if (result.stepId) {
        uiStore.setPendingStepDeletion(true)
      }
    }

    props.closeModal()
  }

  const isMutating = props.stepCreateMutation?.isPending || props.contentUpdateMutation?.isPending

  return (
    <>
      <Box className={commonClasses.stickyModalFooterContentWrapper}>
        <Grid gap="xxxl">
          <Grid.Col span={12}>
            <MarkdownEditor
              defaultValue={initialContent}
              onChange={handleContentChange}
              readOnly={isMutating}
              className={clsx(isMutating && 'opacity-60 pointer-events-none')}
              style={{
                minHeight: '50vh',
              }}
            />
            {contentError && (
              <Text size="md" c="red" my={8}>
                {contentError}
              </Text>
            )}
          </Grid.Col>
        </Grid>
      </Box>

      <Modal.Header className={commonClasses.stickyModalFooter}>
        <Group gap="xl">
          <Button leftSection={<IconDeviceFloppy />} onClick={createCM} loading={isMutating} disabled={!!contentError}>
            Save Content
          </Button>
          {!isMutating && (
            <Button size="sm" variant="light" color="gray" onClick={() => props.closeModal()}>
              Cancel
            </Button>
          )}
        </Group>
      </Modal.Header>
    </>
  )
}
